import { useSettingsStore } from "@/lib/settings-store"
import { useSourceStore, type SourceConfig } from "@/lib/source-store"
import { useScriptOverlayStore } from "@/lib/script-overlay-settings-store"

/**
 * Everything a Backup & Restore (and the cloud sync) must carry beyond the original core list: the settings added
 * since the backup was written, which sources are on, custom sources, and the script overlay look.
 * Shared by components/settings-backup.tsx and hooks/use-user-data-sync.ts so the two cannot drift apart again.
 *
 * Deliberately NOT included (secrets or things that must not flip silently from a file): universalPassword,
 * universalApiKey, appLocked, appLockCode, cloudSyncEnabled, per-source API keys/ids/passwords, the unfiltered-content
 * toggle and Joymode (spends credits).
 */
export const EXTRA_SETTING_KEYS = [
  "infinitePagesMode", "pullToLoadEnabled", "pullDistanceThreshold", "activeRatings", "gridVideoPreviews", "forcePreview", "albumHoverPreview", "tagStepGesture", "tagStepEnd",
  "floatingButtonsAutoHide", "floatingButtonsAutoHideDelay", "stickyHeader", "clearClipboardAfter", "autoLockAfter",
  "booruModalLayout", "eromeModalPositioning", "filterAIImages", "tagsScrollableMode",
  // Storage & Download section
  "saveLocation", "customSavePath", "createSubfolder", "autoDownloadWithoutDialog",
  "videoPitchCorrection", "videoDefaultSpeed", "videoAutoMute", "searchMode", "nestedGridMode", "nestedGridThreshold",
  "perSourceNestedMode", "showPointGapTooltip", "handyPreviewPlaybackMode",
  "gridFitShape", "gridFitMode", "gridFitBlur",
] as const

const CREDENTIAL_FIELDS = ["apiKey", "userId", "password"] as const

export interface BackupExtras {
  settings: Record<string, unknown>
  sources: { id: string; enabled: boolean }[]
  customSources: Partial<SourceConfig>[]
  scriptOverlay: Record<string, unknown>
}

const isPlain = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v)

export function collectExtras(): BackupExtras {
  const state = useSettingsStore.getState() as unknown as Record<string, unknown>
  const settings: Record<string, unknown> = {}
  for (const k of EXTRA_SETTING_KEYS) if (state[k] !== undefined && typeof state[k] !== "function") settings[k] = state[k]

  const all = useSourceStore.getState().sources
  const sources = all.map((s) => ({ id: s.id, enabled: !!s.enabled }))
  const customSources = all
    .filter((s) => s.isDefault === false)
    .map((s) => {
      const copy: Record<string, unknown> = { ...s }
      for (const f of CREDENTIAL_FIELDS) delete copy[f]
      delete copy.id
      delete copy.createdAt
      delete copy.updatedAt
      return copy as Partial<SourceConfig>
    })

  const overlayState = useScriptOverlayStore.getState() as unknown as Record<string, unknown>
  const scriptOverlay: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(overlayState)) if (typeof v !== "function") scriptOverlay[k] = v

  return { settings, sources, customSources, scriptOverlay }
}

/** Applies extras from a backup or cloud payload. Every value is type-checked against the live store, so a bad or
 * old file can never write the wrong kind of value. */
export function applyExtras(raw: unknown) {
  if (!isPlain(raw)) return
  const extras = raw as Partial<BackupExtras>

  if (isPlain(extras.settings)) {
    const current = useSettingsStore.getState() as unknown as Record<string, unknown>
    const patch: Record<string, unknown> = {}
    for (const k of EXTRA_SETTING_KEYS) {
      const v = (extras.settings as Record<string, unknown>)[k]
      if (v === undefined || current[k] === undefined) continue
      if (Array.isArray(current[k]) ? Array.isArray(v) : typeof v === typeof current[k]) patch[k] = v
    }
    if (Object.keys(patch).length) useSettingsStore.setState(patch as never)
  }

  const store = useSourceStore.getState()
  if (Array.isArray(extras.sources)) {
    for (const s of extras.sources) {
      if (!isPlain(s) || typeof s.id !== "string" || typeof s.enabled !== "boolean") continue
      if (store.sources.some((x) => x.id === s.id)) store.updateSource(s.id, { enabled: s.enabled })
    }
  }
  if (Array.isArray(extras.customSources)) {
    for (const c of extras.customSources) {
      if (!isPlain(c) || typeof c.name !== "string") continue
      if (useSourceStore.getState().sources.some((x) => x.name === c.name)) continue
      useSourceStore.getState().addSource(c as Omit<SourceConfig, "id" | "createdAt" | "updatedAt">)
    }
  }

  if (isPlain(extras.scriptOverlay)) {
    const current = useScriptOverlayStore.getState() as unknown as Record<string, unknown>
    const patch: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(extras.scriptOverlay)) {
      if (typeof current[k] === "function" || current[k] === undefined) continue
      if (Array.isArray(current[k]) ? Array.isArray(v) : typeof v === typeof current[k]) patch[k] = v
    }
    if (Object.keys(patch).length) useScriptOverlayStore.setState(patch as never)
  }
}
